import {
    ChangeDetectorRef,
    Component,
    forwardRef,
    Input,
    OnInit,
    ChangeDetectionStrategy,
    inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { QueryObserverResult } from '@tanstack/query-core';
import { AsyncPipe } from '@angular/common';
import { BehaviorSubject, filter, Observable, take, switchMap } from 'rxjs';
import { LabelModule } from '@progress/kendo-angular-label';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { Region } from '../../models/location/region';
import { LocationDataService } from '../../services/location-data.service';
import { VirtualizationSettings } from '@progress/kendo-angular-dropdowns';

@Component({
    selector: 'app-regions-filter',
    templateUrl: './regions-filter.component.html',
    styleUrls: ['./regions-filter.component.scss'],
    imports: [AsyncPipe, LabelModule, DropDownsModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => RegionsFilterComponent),
            multi: true,
        },
    ],
})
export class RegionsFilterComponent implements ControlValueAccessor, OnInit {
    private locationDataService = inject(LocationDataService);
    private cdr = inject(ChangeDetectorRef);

    public regionsQueryResults$?: Observable<
        QueryObserverResult<Region[], Error>
    >;

    public value: string[] = [];
    public isDisabled: boolean = false;
    public virtual: VirtualizationSettings = { itemHeight: 28 };

    private onChange: (value: string[]) => void = () => {};
    private onTouched: () => void = () => {};

    private countriesSubject = new BehaviorSubject<string[]>([]);

    @Input() set countries(value: string[]) {
        this.countriesSubject.next(value ?? []);
    }

    writeValue(value: string[]): void {
        this.value = value ?? [];
        this.cdr.markForCheck();
    }

    registerOnChange(fn: (value: string[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    handleValueChange(value: string[]): void {
        this.value = value;
        this.onChange(value);
    }

    handleBlur(): void {
        this.onTouched();
    }

    ngOnInit(): void {
        this.regionsQueryResults$ = this.locationDataService.dataUpdated$.pipe(
            filter((updated) => updated),
            take(1),
            switchMap(() => this.countriesSubject),
            switchMap(
                (countries) =>
                    this.locationDataService.getRegions('', countries).result$,
            ),
        );
    }
}
